import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { take, catchError } from 'rxjs/operators';
import moment from 'moment';
import { Product } from 'src/app/order/models/product.model';
import { ProductService } from 'src/app/quoting/services/product.service';
import { Warehouse } from '../../models/warehouse.model';
import { WarehouseLocation } from '../../models/warehouse-location.model';
import { WarehouseService } from '../../services/warehouse.service';
import { WarehouseLocationService } from '../../services/warehouse-location.service';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { StockWeightService } from '../../services/stock-weight.service';
import { ReportCsvService } from '../../services/report-csv.service';
import { WarehouseReportingService } from '../../services/warehouse-reporting.service';
import {
  BatchBalance,
  BatchBalancesResult,
  BatchLocationBalance
} from '../../models/warehouse-reporting.model';

/**
 * What this report can be narrowed to. Its own class rather than `WarehousingReportFilters`, which
 * four other screens share and which carries dates and a movement type that mean nothing here.
 */
export class BatchReportFilters {
  /** -1 means "all", matching the selects on every other warehousing screen. */
  warehouseId: number = -1;
  warehouseLocationId: number = -1;
  productId: number = -1;

  /** Free text, matched exactly by the endpoint. Blank means every batch. */
  batchNumber: string = '';
}

@Component({
    selector: 'app-report-batch-traceability',
    templateUrl: './report-batch-traceability.component.html',
    styleUrls: ['./report-batch-traceability.component.scss'],
    standalone: false
})
export class ReportBatchTraceabilityComponent implements OnInit {

  searchFilters: BatchReportFilters = new BatchReportFilters();

  warehouses: Warehouse[] = [];
  locations: WarehouseLocation[] = [];
  products: Product[] = [];

  /**
   * The whole payload rather than its parts spread across fields, because the parts only mean
   * anything together: `rows` is what carries a batch, `unbatchedDetail` is what does not, and
   * `locations` is the only place the two are put beside the total they make up.
   */
  result: BatchBalancesResult = new BatchBalancesResult();

  /** Distinguishes "not run yet" from "ran and found nothing". */
  hasRun: boolean = false;
  running: boolean = false;

  constructor(private warehouseReportingService: WarehouseReportingService,
    private warehouseService: WarehouseService,
    private warehouseLocationService: WarehouseLocationService,
    private productService: ProductService,
    private stockQuantityService: StockQuantityService,
    private stockWeightService: StockWeightService,
    private reportCsvService: ReportCsvService,
    private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.warehouseService.getAllWarehouses()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouses');
      return e;
    }))
    .subscribe((data: Warehouse[]) => {
      this.warehouses = data;
    })

    this.productService.getAllActiveProducts()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load products');
      return e;
    }))
    .subscribe((data: Product[]) => {
      this.products = data;
    })

    // A recall starts with a number on a pallet, so the batch list links straight in with one.
    this.route.queryParams
    .pipe(take(1))
    .subscribe((params) => {
      if (params['productId']) {
        this.searchFilters.productId = +params['productId'];
      }

      if (params['batchNumber']) {
        this.searchFilters.batchNumber = params['batchNumber'];
      }

      if (params['productId'] || params['batchNumber']) {
        this.loadReport();
      }
    })
  }

  /**
   * Locations are scoped by warehouseId and the same aisle/shelf exists at more than one site, so
   * the location list only becomes meaningful once a warehouse is chosen.
   */
  onWarehouseChanged(warehouseId: any) {
    this.searchFilters.warehouseId = +warehouseId;
    this.searchFilters.warehouseLocationId = -1;
    this.locations = [];

    if (this.searchFilters.warehouseId === -1) {
      return;
    }

    this.warehouseLocationService.getWarehouseLocationsForWarehouse(this.searchFilters.warehouseId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouse locations');
      return e;
    }))
    .subscribe((data: WarehouseLocation[]) => {
      this.locations = data;
    })
  }

  loadReport() {
    this.running = true;

    this.warehouseReportingService.getBatchBalances(this.searchFilters)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      this.running = false;
      alert('could not get report');
      return e;
    }))
    .subscribe((data: BatchBalancesResult) => {
      this.result = this.normalise(data);
      this.hasRun = true;
      this.running = false;
    })
  }

  /**
   * Fills in any part of the payload that did not arrive, field by field.
   *
   * The same defensiveness `report-stock-on-hand` applies to its own body, for the same reason: a 200
   * carrying an unexpected shape would otherwise blank the whole screen on a template error rather
   * than draw what it did get. `enforced` defaults to FALSE when absent, which is the safe direction —
   * an absent flag must not be read as permission to describe these figures as a control.
   */
  private normalise(data: BatchBalancesResult): BatchBalancesResult {
    const result: BatchBalancesResult = new BatchBalancesResult();

    if (!data) {
      return result;
    }

    result.rows = data.rows ? data.rows : [];
    result.unbatchedDetail = data.unbatchedDetail ? data.unbatchedDetail : [];
    result.locations = data.locations ? data.locations : [];
    result.negativeBatches = data.negativeBatches ? data.negativeBatches : 0;
    result.enforced = data.enforced === true;

    return result;
  }

  /** True once the report has anything at all to draw, in any of its three parts. */
  hasResults(): boolean {
    return this.result.rows.length > 0
      || this.result.unbatchedDetail.length > 0
      || this.result.locations.length > 0;
  }

  /**
   * Whether ANY location in the result holds stock that carries no batch number.
   *
   * Drives the reconciliation note rather than hiding it, because this is the case in which batch
   * balances cannot add up to a location's stock level — and it is arithmetic rather than a defect.
   */
  hasUnbatchedStock(): boolean {
    return this.result.locations.some((row: BatchLocationBalance) => !this.stockQuantityService.isZero(row.unbatchedQty));
  }

  /** Server-classified against each group's own tolerance. Never re-derive: see `BatchBalance`. */
  isNegative(row: BatchBalance): boolean {
    return row.isNegative;
  }

  /**
   * Composite key: a row is a (batch, product, location) group and carries no id of its own — there
   * is no batch table, so the number is all there is. Null on every remainder row, so the word is
   * used rather than an empty segment, which would collide with a batch numbered ''.
   */
  rowKey(row: BatchBalance): string {
    return (row.batchNumber === null ? 'unbatched' : row.batchNumber)
      + '@' + row.productId + '@' + row.warehouseLocationId;
  }

  locationKey(row: BatchLocationBalance): string {
    return row.productId + '@' + row.warehouseLocationId;
  }

  /**
   * The number this row is traced by.
   *
   * There is no "deleted batch" case to label any more, and that is a consequence of there being no
   * batch table: a batch number exists exactly as long as a movement carries it, so it cannot outlive
   * a record that was never kept separately. A null number therefore means one thing only — this is a
   * remainder row rather than a batch — and `isUnbatched` is what says so. The word is used rather
   * than an empty cell so a remainder can never be mistaken for a batch whose number failed to load.
   */
  batchLabel(row: BatchBalance): string {
    return row.batchNumber === null || row.batchNumber === undefined
      ? 'No batch number'
      : row.batchNumber;
  }

  formatQty(qty: number): string {
    return this.stockQuantityService.round(qty).toString();
  }

  /**
   * A batch's (or a remainder group's) net weight, blank where nothing in the group was weighed
   * at both ends — the same rule `report-adjustments` and `report-movement-history` apply, read
   * off the same backend figure rather than re-derived from the rows here.
   */
  formatWeight(weight: number): string {
    return this.stockWeightService.format(weight);
  }

  /** True when part of this group was not weighed at both ends, so its net weight is partial. */
  hasUnweighed(row: BatchBalance): boolean {
    return row.unweighedMovements > 0;
  }

  formatDateTime(value: string): string {
    return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '';
  }

  /**
   * One file covering every part of the report, with a `Row Type` column, rather than four exports.
   *
   * The remainder is exported as its own row for the same reason it is drawn as its own figure: a
   * spreadsheet that carried only the batch rows would let a reader total them and believe the total
   * was the location's stock.
   */
  exportToCSV() {
    const headers: string[] = ['Row Type', 'Batch', 'Product', 'SKU', 'Warehouse', 'Location',
      'Qty', 'Movements', 'Revaluations', 'Negative', 'Net Weight', 'Weighed', 'Unweighed', 'Last Movement'];

    const balanceRow = (row: BatchBalance, type: string) => [
      type,
      row.isUnbatched ? '' : this.batchLabel(row),
      row.productName,
      row.sku,
      row.warehouseName,
      row.locationLabel,
      this.formatQty(row.qty),
      row.movements,
      row.revaluations,
      row.isNegative ? 'YES' : 'no',
      this.formatWeight(row.netWeight),
      row.weighedMovements,
      row.unweighedMovements,
      this.formatDateTime(row.lastMovementAt)
    ];

    const rows: any[][] = this.result.rows.map((row: BatchBalance) => balanceRow(row, 'batch'));

    this.result.unbatchedDetail.forEach((row: BatchBalance) => {
      rows.push(balanceRow(row, 'un-batched remainder'));
    });

    // BatchLocationBalance deliberately carries no weight (see the model's own comment on why a
    // weight has no place in this reconciliation table), so these rows pad the three weight
    // columns with blanks rather than a figure that was never computed for them.
    this.result.locations.forEach((row: BatchLocationBalance) => {
      rows.push(['location total', '', row.productName, row.sku, row.warehouseName, row.locationLabel,
        this.formatQty(row.ledgerQty), '', '', '', '', '', '', '']);
      rows.push(['location batched', '', row.productName, row.sku, row.warehouseName, row.locationLabel,
        this.formatQty(row.batchedQty), '', '', '', '', '', '', '']);
      rows.push(['location un-batched', '', row.productName, row.sku, row.warehouseName, row.locationLabel,
        this.formatQty(row.unbatchedQty), '', '', '', '', '', '', '']);
    });

    // No "batch never moved" section. With no batch table a batch cannot exist without a movement -
    // being typed onto a movement is the only way one comes into existence - so the endpoint has
    // nothing to report there and never sends it.

    this.reportCsvService.download('batch-traceability', headers, rows);
  }
}
